import type {CastReceiverContext, PlayerManager} from 'chromecast-caf-receiver/cast.framework';
import type {Track} from 'chromecast-caf-receiver/cast.framework.messages';

import {CAFv3AnalyticsStateMachine} from '../../analyticsStateMachines/CAFv3AnalyticsStateMachine';
import type {Analytics} from '../../core/Analytics';
import {HeartbeatService} from '../../core/HeartbeatService';
import {Event} from '../../enums/Event';
import {Player} from '../../enums/Player';
import {getStreamTypesFromURL} from '../../enums/StreamTypes';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import type {Feature} from '../../features/Feature';
import type {FeatureConfig} from '../../features/FeatureConfig';
import type {AnalyticsConfig} from '../../types/AnalyticsConfig';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import type {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {buildPlaybackInfoStreamTypeFields, normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import {isBlank} from '../../utils/stringUtils';
import {isNotNullish, isNullish} from '../../utils/Utils';
import {InternalAdapter} from '../internal/InternalAdapter';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {CastFrameworkEvent} from './castFrameworkTypes';

export class CAFv3InternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private readonly playerManager: PlayerManager;

  /** Boolean flag to prevent multiple calls of the {@link Event.UNLOAD} event */
  private onBeforeUnLoadEvent = false;
  private activeTextTracks: Track[] = [];

  constructor(castReceiverContext: CastReceiverContext, opts?: AnalyticsStateMachineOptions) {
    super(opts);
    const playingHeartbeatService = new HeartbeatService(() =>
      castReceiverContext.getPlayerManager().getCurrentTimeSec(),
    );
    const stateMachine = new CAFv3AnalyticsStateMachine(this.stateMachineCallbacks, playingHeartbeatService, this.opts);
    playingHeartbeatService.setListener(stateMachine);
    this.playerManager = castReceiverContext.getPlayerManager();
    this.stateMachine = stateMachine;
  }

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    this.registerCastFrameworkEventListeners();
    this.registerWindowUnloadEventListeners();

    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );
    return [errorDetailTracking];
  }

  getPlayerVersion = () => cast?.framework?.VERSION ?? 'unknown';
  getPlayerName = () => Player.CHROMECAST_SHAKA;
  getPlayerTech = () => 'html5';
  getAutoPlay = () => this.playerManager.getQueueManager()?.getCurrentItem()?.autoplay ?? false;
  getDrmPerformanceInfo = (): DrmPerformanceInfo | undefined => this.drmPerformanceInfo;

  sourceChange(config: AnalyticsConfig) {
    this.stateMachine.callManualSourceChangeEvent(config, this.playerManager.getCurrentTimeSec());
  }

  getCurrentPlaybackInfo(): PlaybackInfo {
    const stats = this.playerManager.getStats();

    // filter out undefined and null values
    const subtitleLanguages = this.activeTextTracks?.map((t) => t.language).filter(isNotNullish);

    return {
      ...this.getCommonPlaybackInfo(),
      ...detectCAFv3PlaybackInfoStreamTypeFields(this.playerManager),
      ...detectCAFv3PlaybackInfoAudioFields(this.playerManager),
      isLive: detectIsLive(this.playerManager),
      playerTech: this.getPlayerTech(),
      videoDuration: normalizeVideoDuration(this.playerManager.getMediaInformation()?.duration ?? 0),
      videoPlaybackHeight: stats.height,
      videoPlaybackWidth: stats.width,
      subtitleLanguage: subtitleLanguages ? subtitleLanguages.join(',') : undefined,
      // TODO: Implement `droppedFrames` tracking in CAFv3
      //  - https://developers.google.com/cast/docs/reference/web_receiver/cast.framework.stats.Stats#droppedFrames
      droppedFrames: 0,
    };
  }

  private registerCastFrameworkEventListeners() {
    // This is a special identifier, which can be used to listen for all events (mostly used for debugging purposes)
    // see: https://developers.google.com/cast/docs/reference/web_receiver/cast.framework.events#.EventType
    // this.playerManager.addEventListener(CastFrameworkEvent.ALL, (event) => {
    //   console.log(`⭐ [${event.type}]`, JSON.stringify(event));
    // });

    // Fired when the browser has finished loading the metadata for a clip. This event is forwarded from the MediaElement, and has been wrapped.
    this.playerManager.addEventListener(CastFrameworkEvent.LOADED_METADATA, (_event) => {
      this.eventCallback(Event.SOURCE_LOADED, {});
    });

    // Fired when the player has finished processing a load request and is ready to play.
    this.playerManager.addEventListener(CastFrameworkEvent.PLAYER_LOAD_COMPLETE, () => {
      // captions
      const textTracksManager = this.playerManager.getTextTracksManager();
      this.activeTextTracks = textTracksManager.getActiveTracks();

      this.eventCallback(Event.READY, {});
    });

    // Fired when playback is ready to start (ie: after being paused). This event is forwarded from the MediaElement, and has been wrapped.
    this.playerManager.addEventListener(CastFrameworkEvent.PLAY, (event) => {
      this.eventCallback(Event.PLAY, {currentTime: event.currentMediaTime});
    });

    // Fired when playback has started. This event is forwarded from the MediaElement, and has been wrapped in a cast.framework.events.MediaElementEvent.
    this.playerManager.addEventListener(CastFrameworkEvent.PLAYING, (_event) => {
      this.eventCallback(Event.PLAYING, {currentTime: this.currentTime});
    });

    // Fired periodically while the media is playing. This will fire whenever the currentTime attribute changes
    this.playerManager.addEventListener(CastFrameworkEvent.TIME_UPDATE, (event) => {
      this.eventCallback(Event.TIMECHANGED, {currentTime: event.currentMediaTime});
    });

    // Fired when playback is paused. This event is forwarded from the MediaElement, and has been wrapped.
    this.playerManager.addEventListener(CastFrameworkEvent.PAUSE, (_event) => {
      this.eventCallback(Event.PAUSE, {currentTime: this.currentTime});
    });

    // Fired when an error occurs
    this.playerManager.addEventListener(CastFrameworkEvent.ERROR, (event) => {
      const errorData = isNotNullish(event.error) ? event.error : undefined;
      this.eventCallback(Event.ERROR, {
        currentTime: this.playerManager.getCurrentTimeSec(),
        code: event.detailedErrorCode,
        message: event.reason,
        legacyData: errorData,
        originalError: event,
        data: {additionalData: JSON.stringify(errorData)},
      });
    });

    // Fired when the media is being seeked. This event is forwarded from the MediaElement, and has been wrapped.
    this.playerManager.addEventListener(CastFrameworkEvent.SEEKING, (event) => {
      this.eventCallback(Event.SEEK, {currentTime: event.currentMediaTime});
    });

    // Fired when a seek has finished. This will not be fired in case seeking finishes when the media is paused. This event is forwarded from the MediaElement, and has been wrapped.
    this.playerManager.addEventListener(CastFrameworkEvent.SEEKED, (event) => {
      this.eventCallback(Event.SEEKED, {currentTime: event.currentMediaTime});
    });

    this.playerManager.addEventListener(CastFrameworkEvent.BUFFERING, (event) => {
      if (event.isBuffering) {
        this.eventCallback(Event.START_BUFFERING, {currentTime: this.currentTime});
      }
    });

    // Fired when a LOAD message is received
    //
    // Why REQUEST_LOAD and not LOAD_START like in HTML5 adapters?
    //  Later after the REQUEST_LOAD, also the LOAD_START event is always called. However, we use REQUEST_LOAD,
    //  because TIME_UPDATE event happens between before the LOAD_START, and that would wrongly move us to PLAYING state.
    //  For example:
    //    If we would be in PAUSE state and relay on LOAD_START event, at first the TIME_UPDATE would happen
    //    and move us to PLAYING state and only after that we would call sourceChange and be SOURCE_CHANGE state,
    //    but this would generate short playing sample before the source change from PAUSE state.
    this.playerManager.addEventListener(CastFrameworkEvent.REQUEST_LOAD, (_event) => {
      this.sourceChange({});
    });
  }

  private registerWindowUnloadEventListeners() {
    const handleWindowUnloadEvent = () => {
      if (!this.onBeforeUnLoadEvent) {
        this.onBeforeUnLoadEvent = true;
        this.eventCallback(Event.UNLOAD, {currentTime: this.currentTime});
      }
      this.release();
    };

    this.windowEventTracker.addEventListener('beforeunload', handleWindowUnloadEvent);
    this.windowEventTracker.addEventListener('unload', handleWindowUnloadEvent);
  }

  protected get currentTime(): number {
    return this.playerManager.getCurrentTimeSec();
  }
}

function detectIsLive(playerManager: PlayerManager): true | undefined {
  // needs to be explicitly set to "LIVE" by customers in their receiver implementation
  if (playerManager.getMediaInformation()?.streamType === 'LIVE') {
    return true;
  }

  // hack because the object exists only for live streams
  const liveSeekableRange = playerManager.getLiveSeekableRange();

  if (liveSeekableRange) {
    return true;
  }

  return undefined;
}

export function detectCAFv3PlaybackInfoStreamTypeFields(
  playerManager: PlayerManager,
): Pick<PlaybackInfo, 'm3u8Url' | 'mpdUrl' | 'progUrl' | 'streamFormat'> {
  // Optional media URL, to allow using contentId for the actual ID. If contentUrl is provided, it is used as
  // the media URL. Otherwise, the contentId is used as the media URL.
  // see: https://developers.google.com/cast/docs/reference/web_receiver/cast.framework.messages.MediaInformation#contentUrl
  let url = playerManager.getMediaInformation()?.contentUrl;
  if (isNullish(url) || isBlank(url)) {
    url = playerManager.getMediaInformation()?.contentId;
  }

  const streamType = getStreamTypesFromURL(url);

  return buildPlaybackInfoStreamTypeFields(streamType, url);
}

function detectCAFv3PlaybackInfoAudioFields(
  playerManager: PlayerManager,
): Pick<PlaybackInfo, 'audioBitrate' | 'audioCodec' | 'audioLanguage'> {
  try {
    const audioTracksManager = playerManager.getAudioTracksManager();

    // getActiveTrack() can throw error if there is no video loaded yet, or no active audio track
    const activeTract = audioTracksManager.getActiveTrack();

    return {
      audioLanguage: activeTract?.language,
      audioCodec: activeTract?.audioTrackInfo?.audioCodec,
      audioBitrate: undefined, // bitrate is not available in CAFv3
    };
  } catch (_error) {
    // no action needed
    // active track is not available, so we cannot get audio fields
    return {};
  }
}
